/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Tabs,
  Stack,
  Group,
  Avatar,
  Text,
  Paper,
  ActionIcon,
  Box,
  Divider,
  Button,
  Center,
  Menu,
  Select,
  Indicator,
  Tooltip,
  ScrollArea,
  Modal,
  Loader,
} from "@mantine/core";
import {
  Bell,
  Clock,
  Dots,
  Notification,
  Checklist,
  DeviceAnalytics,
} from "tabler-icons-react";
import {
  NotificationResponse,
  useGetNotifications,
} from "api/notifications/useNotifications";
import NotFound from "components/NotFound";
import { useNavigate } from "react-router-dom";
import { useUser } from "hooks/useUser";
import { useUpdateNotificationsRead } from "api/notifications/noficationMark";
import { io, Socket } from "socket.io-client";
import { useApproveUser } from "api/users/approvedadmin";
import { showNotification } from "@mantine/notifications";
import { useGetUserPermissions } from "api/permission/getpermission";

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: string;
  avatar?: string;
  userName?: string;
  isRead?: boolean;
  hasIndicator?: boolean;
  priority?: string;
  project?: string;
  projectId?: {
    _id: string;
    name: string;
  };
  issueId?: string;
  color?: string;
  createdAt?: string;
  newUserId?: string;
  // optional if your API includes a user id reference
  userId?: string;
  createdBy?: string;
}

const formatTimeAgo = (timestamp: string): string => {
  try {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInSeconds = Math.floor(
      (now.getTime() - notificationTime.getTime()) / 1000
    );

    if (diffInSeconds < 60) return "just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  } catch {
    return timestamp;
  }
};

export function Notifications() {
  const attendanceTypes = [
    "attendance_late",
    "attendance_late_escalated",
    "attendance_consecutive_late",
  ];

  const leaveTypes = [
    "leave_request_rejected",
    "leave_request_approved",
    "leave_request_created",
  ];

  const navigate = useNavigate();
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetNotifications();

  const { mutate: approveUser } = useApproveUser();

  const { user } = useUser();
  const userRole = user?.role ?? "";
  console.log(userRole);
  const userId = user?._id ?? "";
  const updateMut = useUpdateNotificationsRead(userId);

  const [statusFilter, setStatusFilter] = useState<"all" | "read" | "unread">(
    "all"
  );
  const [unreadCount, setUnreadCount] = useState(0);
  const socketRef = useRef<Socket>();

  const [modalOpened, setModalOpened] = useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<NotificationItem | null>(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);

  const notifications: NotificationItem[] = useMemo(
    () =>
      data?.pages?.flatMap(
        (page: NotificationResponse) => page.notifications
      ) || [],
    [data]
  );

  const { data: permResp } = useGetUserPermissions(userId);
  const permissions = permResp?.data?.permissions ?? {};

  useEffect(() => {
    if (notifications.length > 0) {
      const initial = notifications.filter((n) => !n.isRead).length;
      setUnreadCount(initial);
    }
  }, [notifications]);

  useEffect(() => {
    socketRef.current = io(process.env.REACT_APP_SOCKET_URL || "/");
    socketRef.current.on("unreadNotificationsCount", (newCount: number) => {
      console.log("Unread count updated:", newCount);
      setUnreadCount(newCount);
    });
    return () => {
      socketRef.current?.disconnect();
    };
  }, []);

  const pagination = data?.pages?.[data.pages.length - 1]?.pagination;

  if (isLoading) {
    return (
      <Center style={{ height: 200 }}>
        <Text>Loading notifications...</Text>
      </Center>
    );
  }

  if (isError) {
    return <NotFound />;
  }

  const filterByStatus = (list: NotificationItem[]) => {
    if (statusFilter === "all") return list;
    if (statusFilter === "read") return list.filter((n) => n.isRead);
    return list.filter((n) => !n.isRead);
  };

  const openModalForNotification = (notification: NotificationItem) => {
    setSelectedNotification(notification);
    setLoadingUserDetails(false);
    setModalOpened(true);
  };

  const handleApprove = () => {
    if (!selectedNotification) return;
    const targetId =
      (selectedNotification as any).newUserId ??
      (selectedNotification as any).newUserId ??
      selectedNotification.newUserId;

    try {
      approveUser({ id: targetId });
    } catch (err) {
    } finally {
      setModalOpened(false);
      setSelectedNotification(null);
    }
  };

  const handleReject = () => {
    setModalOpened(false);
  };

  const getNested = (obj: any, path: string) => {
    if (!obj || !path) return undefined;
    return path
      .split(".")
      .reduce(
        (acc: any, key: string) =>
          acc && acc[key] !== undefined ? acc[key] : undefined,
        obj
      );
  };

  const hasAccess = (item: any) => {
    if (item.permissionKey) {
      if (Array.isArray(item.permissionKey)) {
        return item.permissionKey.some((p: any) => {
          const v = getNested(permissions, p);
          return (
            v === true ||
            (typeof v === "object" && Object.values(v).some(Boolean))
          );
        });
      } else {
        const v = getNested(permissions, item.permissionKey);
        return (
          v === true ||
          (typeof v === "object" && Object.values(v).some(Boolean))
        );
      }
    }

    if (item.allowedRoles && item.allowedRoles.includes(user.role || ""))
      return true;

    return false;
  };

  const showNoPermission = () => {
    showNotification({
      color: "red",
      title: "Access denied",
      message: "You do not have permission to access this page",
    });
  };

  const navigateWithPermission = (
    permissionKey: string | string[],
    path: string
  ) => {
    const allowed = hasAccess({ permissionKey } as any);

    if (!allowed) {
      showNoPermission();
      return;
    }

    navigate(path);
  };

  const renderNotificationItem = (notification: NotificationItem) => (
    <Paper
      key={notification.id}
      p="md"
      style={{
        border: "none",
        backgroundColor: notification.isRead
          ? "transparent"
          : "var(--mantine-color-blue-0)",

        cursor: "pointer",
      }}
    >
      <Group position="apart" align="flex-start" spacing="sm">
        <Group
          align="flex-start"
          spacing="sm"
          style={{ flex: 1 }}
          onClick={() => {
            updateMut.mutate({
              notificationIds: [notification.id],
              read: true,
            });

            if (attendanceTypes.includes(notification.type)) {
              if (userRole === "member") {
                navigateWithPermission(
                  "attendance.employee",
                  "/attendance-employee"
                );
              } else {
                navigateWithPermission("attendance.admin", "/attendance-admin");
              }
              return;
            }

            if (leaveTypes.includes(notification.type)) {
              if (userRole === "member") {
                navigateWithPermission(
                  "employees.leaveEmployees",
                  "/leave-employees"
                );
              } else {
                navigateWithPermission("employees.adminLeave", "/admin-leave");
              }
              return;
            }

            // existing behavior remains untouched
            if (notification.type === "Requested to join") {
              openModalForNotification(notification);
            }
          }}
        >
          <Box style={{ position: "relative" }}>
            <Avatar size={40} radius="xl" color="blue">
              {notification.avatar ? (
                <Avatar>{notification.avatar}</Avatar>
              ) : (
                <Bell size={20} />
              )}
            </Avatar>
            {notification.hasIndicator && (
              <Box
                style={{
                  position: "absolute",
                  top: -2,
                  right: -2,
                  width: 12,
                  height: 12,
                  backgroundColor: "#1971c2",
                  borderRadius: "50%",
                  border: "2px solid white",
                }}
              />
            )}
          </Box>

          <Box style={{ flex: 1 }}>
            <Text size="sm" weight={500} color="dark" mb={4}>
              {`${notification?.project ?? ""} (${notification.title})`}
            </Text>
            <Text size="sm" color="dimmed" mb={8}>
              {notification.description}
            </Text>
            <Group align="center" spacing={4}>
              <Clock size={14} color="#868e96" />
              <Text size="xs" color="dimmed">
                {notification.timestamp}
              </Text>
            </Group>
          </Box>
        </Group>
        <Menu shadow="sm" width={150}>
          <Menu.Target>
            <ActionIcon size="sm" variant="subtle" color="gray">
              <Dots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              onClick={() =>
                updateMut.mutate({
                  notificationIds: [notification.id],
                  read: false,
                })
              }
              disabled={updateMut.isLoading}
            >
              UnRead
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                updateMut.mutate({
                  notificationIds: [notification.id],
                  read: true,
                })
              }
              disabled={updateMut.isLoading}
            >
              Read
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Paper>
  );

  const renderNotificationList = (list: NotificationItem[]) => (
    <Stack spacing={0}>
      {list.length === 0 ? (
        <Center style={{ height: 200 }}>
          <Text color="dimmed">No notifications found</Text>
        </Center>
      ) : (
        filterByStatus(list).map((notification, index, arr) => (
          <React.Fragment key={notification.id}>
            {renderNotificationItem(notification)}
            {index < arr.length - 1 && (
              <Divider ml={68} mr="md" color="#f1f3f4" />
            )}
          </React.Fragment>
        ))
      )}
    </Stack>
  );

  return (
    <>
      <Group
        style={{
          position: "absolute",
          right: 46,
        }}
      >
        <Select
          data={[
            { value: "all", label: "All" },
            { value: "read", label: "Read" },
            { value: "unread", label: "Unread" },
          ]}
          value={statusFilter}
          onChange={(value) =>
            setStatusFilter(value as "all" | "read" | "unread")
          }
          size="sm"
          sx={{ width: 120 }}
        />
      </Group>

      <Tabs defaultValue="all">
        <Tabs.List
          style={{
            borderBottom: "1px solid #e9ecef",
          }}
        >
          <Tabs.Tab value="all" icon={<Notification size={14} />}>
            All ({notifications.length})
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="all" pt="xs">
          <ScrollArea style={{ height: "90vh" }}>
            {renderNotificationList(notifications)}
          </ScrollArea>
        </Tabs.Panel>
      </Tabs>

      {notifications.length > 0 && (
        <Center mt="lg" mb="md">
          <Button
            size="md"
            variant="outline"
            onClick={() => fetchNextPage()}
            disabled={!hasNextPage}
            loading={isFetchingNextPage}
          >
            Load More
          </Button>
        </Center>
      )}

      <Modal
        opened={modalOpened}
        onClose={() => {
          setModalOpened(false);
          setSelectedNotification(null);
        }}
        title="User  Details"
        size="lg"
        overflow="inside"
        centered
      >
        {loadingUserDetails ? (
          <Center style={{ minHeight: 160 }}>
            <Loader />
          </Center>
        ) : !selectedNotification ? (
          <Center style={{ minHeight: 160 }}>
            <Stack align="center">
              <Text color="dimmed">No details available</Text>
            </Stack>
          </Center>
        ) : (
          <Stack spacing="md">
            <Group spacing="sm" align="center">
              <Avatar size={56} radius="xl">
                {selectedNotification.avatar ??
                  selectedNotification.userName?.charAt(0)}
              </Avatar>
              <div>
                <Text size="lg" weight={600}>
                  {selectedNotification.userName ?? "—"}
                </Text>
                <Text size="sm" color="dimmed">
                  {selectedNotification.title ?? "—"}
                </Text>
              </div>
            </Group>

            <Divider />

            <Stack spacing={4}>
              <Text size="sm">
                <strong>Description:</strong>{" "}
                {selectedNotification.description ?? "—"}
              </Text>
              <Text size="sm">
                <strong>Received:</strong> {selectedNotification.timestamp}
              </Text>
            </Stack>

            <Group position="right" mt="md" spacing="sm">
              <Button variant="outline" color="red" onClick={handleReject}>
                Reject
              </Button>
              <Button onClick={handleApprove}>Approve</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}

export default Notifications;
